import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'hooks/useForm';
import { updateCashboxPaymentMethods } from '@redux/features/cashboxesSlice';
import { getPaymentMethodsSchema } from 'constants/formValidationSchemas/paymentMethodsSchema';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { Modal } from 'components/Modal';
import styles from '../Cashboxes.scss';

export const PaymentMethodsForm = ({ cashbox, isOpen, setIsOpen }) => {
  const dispatch = useDispatch();

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const onSubmit = async (values) => {
    const submitData = {
      id: cashbox.id,
      methods: values.methods.map((m) => ({ id: m.id, label: m.label })),
    };

    const resultAction = await dispatch(updateCashboxPaymentMethods(submitData));

    if (updateCashboxPaymentMethods.fulfilled.match(resultAction)) {
      setIsOpen(false);
      resetForm();
    }
  };

  const { values, errors, touched, handleBlur, handleSubmit, setFieldValue, resetForm, isValid } = useForm({
    initialValues: { methods: [{ label: '' }] },
    validationSchema: getPaymentMethodsSchema(),
    onSubmit,
    trimValues: true,
    status: {},
  });

  useEffect(() => {
    if (isOpen && cashbox) {
      const additionalMethods = cashbox.methods.filter((m) => m.type !== 'basic');
      setFieldValue('methods', additionalMethods.length > 0 ? additionalMethods : [{ label: '' }]);
    }
  }, [isOpen, cashbox]);

  const handleAdd = () => {
    setFieldValue('methods', [...values.methods, { label: '' }]);
  };

  const handleRemove = (index) => {
    const filtered = values.methods.filter((_, i) => i !== index);
    setFieldValue('methods', filtered);
  };

  const handleMethodChange = (e, index) => {
    const value = e.target.value;
    const newValues = [...values.methods];
    newValues[index] = { ...newValues[index], label: value };
    setFieldValue('methods', newValues);
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={handleClose}>
      <h3>Способи поповнення</h3>
      <p className={styles.methodText}>
        Наразі цей рахунок можна поповнити обравши при внесені коштів{' '}
        {cashbox?.methods?.length > 1 ? (
          'такі способи оплати:'
        ) : (
          <>
            спосіб оплати <span className={styles.method}>{cashbox?.methods?.[0]?.label || 'Невідомо'}</span>
          </>
        )}
      </p>
      {cashbox?.methods?.length > 1 && (
        <ul className={styles.methodsList}>
          {cashbox?.methods?.map((m, index) => (
            <li className={styles.method} key={index}>
              {m.label}
            </li>
          ))}
        </ul>
      )}
      <h4 className={styles.formTitle}>Додаткові способи внесення коштів</h4>
      <form onSubmit={handleSubmit}>
        <div className={styles.dynamicFields}>
          {values.methods?.map((_, index) => (
            <div key={index} className={styles.fieldRow}>
              <Input
                className={styles.dynamicInput}
                value={values.methods[index].label || ''}
                name={`method-${index}`}
                placeholder='Введіть спосіб'
                onChange={(e) => handleMethodChange(e, index)}
                onBlur={handleBlur}
                invalid={touched[`method-${index}`] && errors?.methods?.[index]?.label}
                errorText={touched[`method-${index}`] && errors?.methods?.[index]?.label}
              />
              <button
                className={styles.removeBtn}
                type='button'
                onClick={() => handleRemove(index)}
                aria-label='Видалити спосіб'
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ))}

          <button className={styles.addBtn} type='button' onClick={handleAdd}>
            <FontAwesomeIcon icon={faPlus} /> Додати спосіб
          </button>
        </div>

        <div className={styles.modalActions}>
          <Button className={styles.modalBtn} type='button' onClick={handleClose} ghostStyle smallStyle>
            Скасувати
          </Button>
          <Button className={styles.modalBtn} type='submit' disabled={!isValid} smallStyle>
            Зберегти
          </Button>
        </div>
      </form>
    </Modal>
  );
};
